import type { SendOutcome } from '../lib/api'
import { buildOrderList } from '../lib/orders-list'
import { needsPack } from '../lib/in-flight'
import { OrderCard } from '../components/OrderCard'
import { RefreshIcon } from '../components/Icons'
import { CollectCard, CollectOfflineBanner, LookupBlock, PickupRowContent, ResultBanner, useCollect } from './CollectScreen'
import { FinalCheckBody, FinalCheckButton, FinalCheckConfirm, FinalCheckNotice, useFinalCheck } from './FinalCheckScreen'
import { InFlightBody, InFlightLoadError, InFlightNotices, PackedButton, useInFlight } from './InFlightSection'
import { DriverBar, SendOutNotices, SendOutTick, useSendOut } from './SendOutScreen'
import { CashDueLoadError, CashInBody, CashInButton, CashInNotice, useCashDue } from './CashInCards'
import O from '../locales/orders.json'
import C from '../locales/collect.json'
import F from '../locales/final-check.json'
import S from '../locales/send-out.json'
import { tr, useLang } from '../lib/i18n'

interface OrdersScreenProps {
  isOnline: boolean
  onCollect: (orderId: string, amountUsd: number) => Promise<SendOutcome>
  onCheck: (orderId: string, expectedTotalNow: string) => Promise<SendOutcome>
  /** The ringing order ids, in the ring's order (RingPanel's rows). */
  ringIds: readonly string[]
}

const ERROR_CLASS = 'rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3'
const RETRY_CLASS = 'w-full min-h-tap rounded-lg bg-fleet-700 hover:bg-fleet-800 text-white font-semibold px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2'

// R572 (b1), the till's two faces (Rook #69053): the Orders face. ONE list from the till's four
// reads (ready for pickup, final check with the orders in flight, ready to send), each order once,
// today's only, the ringing ones on top, ONE action per card. The four screens' own hooks and rows
// are the cards' bodies (ruling 3), and Send out keeps its batch: ticks on the cards and one
// "Send with <driver>" bar below the list (ruling 2).
export function OrdersScreen({ isOnline, onCollect, onCheck, ringIds }: OrdersScreenProps) {
  const lang = useLang()
  const c = useCollect(onCollect)
  const fc = useFinalCheck(onCheck)
  const fl = useInFlight({ onPacked: () => fc.load(true), finalCheckOrders: fc.orders })
  const so = useSendOut()
  // R572 (b2): the "Delivered, cash in" cards, from (b0)'s cash-due read.
  const cd = useCashDue(isOnline)

  const now = new Date()
  const entries = buildOrderList(
    { inFlight: fl.orders, finalCheck: fc.orders, pickup: c.tapList, sendOut: so.orders },
    ringIds,
    now,
  )
  const listedIds = new Set(entries.map((e) => e.id))
  const cashDue = [
    ...(cd.due?.outForDelivery ?? []).map((row) => ({ stage: 'outForDelivery' as const, row })),
    ...(cd.due?.deliveredNoRecord ?? []).map((row) => ({ stage: 'deliveredNoRecord' as const, row })),
  ].filter((x) => !listedIds.has(x.row.id))
  const open = c.selected
  const openListed = open !== null && entries.some((e) => e.stage === 'pickup' && e.id === open.id)
  const loading = c.loadingTapList || fc.loading || so.loading || (fl.orders === null && !fl.loadError)
  const failed = c.tapListError || fc.loadError || so.loadError || fl.loadError || cd.failed
  const hasSendOut = entries.some((e) => e.stage === 'sendOut')

  function refreshAll() {
    c.loadTapList()
    fc.load()
    fl.load()
    so.load()
    void cd.load()
  }

  return (
    <div className="min-h-screen bg-fleet-50 px-6 py-8 font-body text-fleet-800">
      <div className="w-full max-w-sm mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="sr-only">{tr(O.ordersTitle, lang)}</h1>
          <span aria-hidden="true" />
          <button
            id="orders-refresh-btn"
            type="button"
            onClick={refreshAll}
            disabled={loading}
            className="min-h-tap inline-flex items-center gap-1.5 rounded-lg px-2 text-base font-semibold text-navy hover:bg-navy-tint disabled:text-fleet-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy"
          >
            <RefreshIcon className={loading ? 'h-4 w-4 shrink-0 animate-spin' : 'h-4 w-4 shrink-0'} />
            <span>{loading ? tr(C.refreshing, lang) : tr(C.refresh, lang)}</span>
          </button>
        </div>

        <CollectOfflineBanner isOnline={isOnline} lang={lang} />
        <InFlightNotices fl={fl} />
        <FinalCheckNotice fc={fc} lang={lang} />
        <SendOutNotices so={so} lang={lang} />
        <CashInNotice cd={cd} lang={lang} />
        {open === null && <ResultBanner c={c} lang={lang} />}

        {open === null && <LookupBlock c={c} lang={lang} />}

        {loading && (
          <p id="orders-loading" className="text-sm text-fleet-500">
            {tr(C.loading, lang)}
          </p>
        )}

        {/* Each read's failure has its own face and retry; one failing read never blanks the rest. */}
        {c.tapListError && (
          <div className="space-y-3">
            <div id="tap-list-error" role="alert" className={ERROR_CLASS}>
              {tr(C.tapListError, lang)}
            </div>
            <button id="tap-list-retry-btn" type="button" onClick={() => c.loadTapList()} className={RETRY_CLASS}>
              {tr(F.tryAgain, lang)}
            </button>
          </div>
        )}
        {fc.loadError && (
          <div className="space-y-3">
            <div id="final-check-load-error" role="alert" className={ERROR_CLASS}>
              {tr(F.finalCheckLoadError, lang)}
            </div>
            <button id="final-check-try-again-btn" type="button" onClick={() => fc.load()} className={RETRY_CLASS}>
              {tr(F.tryAgain, lang)}
            </button>
          </div>
        )}
        <InFlightLoadError fl={fl} />
        <CashDueLoadError cd={cd} lang={lang} />
        {so.loadError && (
          <div className="space-y-3">
            <div id="send-out-load-error" role="alert" className={ERROR_CLASS}>
              {tr(S.sendOutLoadError, lang)}
            </div>
            <button id="send-out-try-again-btn" type="button" onClick={() => so.load()} className={RETRY_CLASS}>
              {tr(F.tryAgain, lang)}
            </button>
          </div>
        )}

        {!loading && !failed && entries.length === 0 && cashDue.length === 0 && open === null && (
          <p id="orders-empty" className="text-sm text-fleet-500">
            {tr(O.ordersEmpty, lang)}
          </p>
        )}

        {(entries.length > 0 || cashDue.length > 0 || open !== null) && (
          <ul id="orders-list" aria-label={tr(O.ordersListLabel, lang)} className="space-y-3">
            {open !== null && !openListed && (
              <OrderCard id={open.id} stage="pickup" ringing={ringIds.includes(open.id)} lang={lang}>
                <CollectCard c={c} lang={lang} />
                <ResultBanner c={c} lang={lang} />
              </OrderCard>
            )}
            {entries.map((entry) => {
              switch (entry.stage) {
                case 'inFlight': {
                  const state = fl.rowStates[entry.id] ?? { kind: 'idle' }
                  return (
                    <OrderCard key={entry.id} id={entry.id} stage="inFlight" ringing={entry.ringing} lang={lang} action={needsPack(entry.order) ? <PackedButton order={entry.order} fl={fl} /> : undefined}>
                      <InFlightBody order={entry.order} state={state} />
                    </OrderCard>
                  )
                }
                case 'finalCheck': {
                  const state = fc.rowStates[entry.id] ?? { kind: 'idle' }
                  return (
                    <OrderCard key={entry.id} id={entry.id} stage="finalCheck" ringing={entry.ringing} lang={lang} action={<FinalCheckButton order={entry.order} fc={fc} lang={lang} />}>
                      <FinalCheckBody order={entry.order} state={state} lang={lang} />
                    </OrderCard>
                  )
                }
                case 'pickup': {
                  if (open !== null && open.id === entry.id) {
                    return (
                      <OrderCard key={entry.id} id={entry.id} stage="pickup" ringing={entry.ringing} lang={lang}>
                        <CollectCard c={c} lang={lang} />
                        <ResultBanner c={c} lang={lang} />
                      </OrderCard>
                    )
                  }
                  return (
                    <OrderCard
                      key={entry.id}
                      id={entry.id}
                      stage="pickup"
                      ringing={entry.ringing}
                      lang={lang}
                      action={
                        <button
                          type="button"
                          data-collect-btn
                          onClick={() => c.selectOrder(entry.order)}
                          className="w-full min-h-tap rounded-lg bg-navy hover:bg-navy/90 text-white font-semibold px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
                        >
                          {tr(O.collectOrder, lang)}
                        </button>
                      }
                    >
                      <div className="flex items-center justify-between gap-3">
                        <PickupRowContent order={entry.order} lang={lang} />
                      </div>
                    </OrderCard>
                  )
                }
                case 'sendOut':
                  return (
                    <OrderCard
                      key={entry.id}
                      id={entry.id}
                      stage="sendOut"
                      ringing={entry.ringing}
                      lang={lang}
                      action={
                        <SendOutTick
                          order={entry.order}
                          isSelected={so.selected.has(entry.id)}
                          onToggle={() => so.toggleOrder(entry.id)}
                          lang={lang}
                        />
                      }
                    />
                  )
              }
            })}
            {cashDue.map(({ stage, row }) => (
              <OrderCard
                key={row.id}
                id={row.id}
                stage={stage}
                ringing={ringIds.includes(row.id)}
                lang={lang}
                action={<CashInButton row={row} cd={cd} lang={lang} />}
              >
                <CashInBody row={row} cd={cd} lang={lang} now={now} />
              </OrderCard>
            ))}
          </ul>
        )}

        {hasSendOut && !so.loading && !so.loadError && <DriverBar so={so} lang={lang} />}
      </div>
      <FinalCheckConfirm fc={fc} lang={lang} fallbackFocusId="orders-refresh-btn" />
    </div>
  )
}
